import { useMemo, useState } from 'react';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';

export default function StorageManager() {
  const { currentOrganizationId } = useOrganization();
  const [q, setQ] = useState('');
  const [mime, setMime] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'deleted'>('active');
  const queryClient = useQueryClient();

  const limit = 50;

  const fetchPage = async ({ pageParam = 0 }: { pageParam?: number }) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(pageParam));
    if (currentOrganizationId) params.set('organizationId', currentOrganizationId);
    if (q) params.set('q', q);
    if (mime) params.set('mime', mime);
    const res = await fetch(`/api/attachments/search?${params.toString()}`, { credentials: 'include', redirect: 'error' });
    if (!res.ok) throw new Error('Failed to load files');
    return res.json() as Promise<{ items: any[] }>;
  };

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['storage-manager', currentOrganizationId, q, mime],
    queryFn: fetchPage,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + (p.items?.length || 0), 0);
      if ((lastPage.items?.length || 0) < limit) return undefined; // end
      return loaded; // use as offset
    },
    enabled: !!currentOrganizationId,
    staleTime: 0,
    initialPageParam: 0,
  });

  const items = useMemo(() => (data?.pages || []).flatMap(p => p.items || []), [data]);

  // Fetch deleted files for recycle bin
  const { data: deletedData, isLoading: deletedLoading, refetch: refetchDeleted } = useQuery({
    queryKey: ['deleted-attachments', currentOrganizationId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (currentOrganizationId) params.set('organizationId', currentOrganizationId);
      const res = await fetch(`/api/attachments/recovery/list?${params.toString()}`, { 
        credentials: 'include', redirect: 'error'
      });
      if (!res.ok) throw new Error('Failed to load deleted files');
      return res.json() as Promise<{ items: any[] }>;
    },
    enabled: !!currentOrganizationId && activeTab === 'deleted',
    staleTime: 0,
  });

  const deletedItems = deletedData?.items || [];

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this file? It will be moved to the Recycle Bin.')) return;
    const res = await fetch(`/api/attachments/${id}`, { method: 'DELETE', credentials: 'include', redirect: 'error' });
    if (res.ok) {
      queryClient.invalidateQueries({ queryKey: ['storage-manager', currentOrganizationId, q, mime] });
      queryClient.invalidateQueries({ queryKey: ['deleted-attachments', currentOrganizationId] });
    } else {
      alert('Delete failed');
    }
  };

  const handleRecover = async (id: string) => {
    const res = await fetch(`/api/attachments/${id}/recover`, {
      method: 'POST',
      credentials: 'include', redirect: 'error'
    });
    if (res.ok) {
      queryClient.invalidateQueries({ queryKey: ['deleted-attachments', currentOrganizationId] });
      queryClient.invalidateQueries({ queryKey: ['storage-manager', currentOrganizationId] });
      alert('File recovered successfully');
    } else {
      try {
        const e = await res.json();
        alert(e?.error || 'Failed to recover file');
      } catch {
        alert('Failed to recover file');
      }
    }
  };

  const handleDeleteForever = async (id: string) => {
    if (!confirm('Permanently delete this file? This cannot be undone.')) return;
    const res = await fetch(`/api/attachments/${id}/forever`, { method: 'DELETE', credentials: 'include', redirect: 'error' });
    if (res.ok) {
      queryClient.invalidateQueries({ queryKey: ['deleted-attachments', currentOrganizationId] });
      alert('File permanently deleted');
    } else {
      try { const e = await res.json(); alert(e?.error || 'Failed to delete forever'); } catch { alert('Failed to delete forever'); }
    }
  };

  const handleDownload = (id: string, inline = false) => {
    const params = new URLSearchParams();
    if (currentOrganizationId) params.set('organizationId', currentOrganizationId);
    if (inline) params.set('inline', '1');
    window.open(`/api/attachments/${id}/download?${params.toString()}`, '_blank');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* HIPAA banner */}
      <div className="mb-3 rounded border border-yellow-700 bg-yellow-900/20 text-yellow-300 px-4 py-3 text-sm">
        HIPAA Notice: BoltOS Storage Manager enforces access controls and audit logging suitable for regulated healthcare environments.{' '}
        <a href="/compliance" className="underline text-yellow-200">Compliance Details</a>
      </div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Storage Manager</h1>
        <a
          href="/settings"
          className="px-3 py-1.5 text-sm rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-white"
        >
          ← Back to Settings
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-neutral-700">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'active'
              ? 'text-white border-b-2 border-blue-500'
              : 'text-neutral-400 hover:text-white'
          }`}
        >
          Active Files
        </button>
        <button
          onClick={() => setActiveTab('deleted')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'deleted'
              ? 'text-white border-b-2 border-blue-500'
              : 'text-neutral-400 hover:text-white'
          }`}
        >
          Recycle Bin
        </button>
      </div>

      {/* Search controls - only show for active files */}
      {activeTab === 'active' && (
        <div className="flex flex-wrap gap-3 mb-4">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by file name"
            className="px-3 py-2 rounded bg-neutral-800 text-white border border-neutral-700"
          />
          <select
            value={mime}
            onChange={(e) => setMime(e.target.value)}
            className="px-3 py-2 rounded bg-neutral-800 text-white border border-neutral-700"
          >
            <option value="">All types</option>
            <option value="image/">Images</option>
            <option value="application/pdf">PDF</option>
            <option value="application/vnd">Office Docs</option>
          </select>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 text-white"
          >
            Search
          </button>
        </div>
      )}

      {/* Active Files Tab */}
      {activeTab === 'active' && (isLoading ? (
          <div className="text-neutral-400">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((f) => {
            const isImage = String(f.mime_type || '').startsWith('image/');
            return (
              <div key={f.id} className="rounded-lg border border-neutral-800 bg-neutral-900 p-3">
                <div className="text-sm text-neutral-300 mb-2 truncate" title={f.file_name}>{f.file_name}</div>
                <div className="text-xs text-neutral-500 mb-2">
                  {(f.size_bytes / 1024 / 1024).toFixed(2)} MB · {f.mime_type}
                </div>
                <div className="rounded bg-neutral-800 overflow-hidden mb-3 flex items-center justify-center" style={{ height: 160 }}>
                  {isImage ? (
                    <img
                      src={`/api/attachments/${f.id}/download?inline=1${currentOrganizationId ? `&organizationId=${currentOrganizationId}` : ''}`}
                      alt={f.file_name}
                      className="object-contain w-full h-full"
                    />
                  ) : (
                    <div className="text-neutral-500 text-sm">No preview</div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownload(f.id, true)}
                    className="flex-1 px-3 py-1.5 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-white text-sm"
                  >
                    Preview
                  </button>
                  <button
                    onClick={() => handleDownload(f.id, false)}
                    className="px-3 py-1.5 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-white text-sm"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => handleDelete(f.id)}
                    className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        ))}

      {/* Load more button for active files */}
      {activeTab === 'active' && (
        <div className="flex justify-center mt-4">
          {hasNextPage && (
            <button
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              className="px-4 py-2 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-white"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}

      {/* Deleted Files Tab (Recycle Bin) */}
      {activeTab === 'deleted' && (
        deletedLoading ? (
          <div className="text-neutral-400">Loading deleted files…</div>
        ) : deletedItems.length === 0 ? (
          <div className="text-neutral-400 text-center py-8">
            <p className="text-lg mb-2">Recycle Bin is empty</p>
            <p className="text-sm">Deleted files will appear here and can be recovered within 30 days</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deletedItems.map((f) => (
              <div key={f.id} className="rounded-lg border border-neutral-800 bg-neutral-900 p-3">
                <div className="text-sm text-neutral-300 mb-2 truncate" title={f.file_name}>{f.file_name}</div>
                <div className="text-xs text-neutral-500 mb-2">
                  {(f.size_bytes / 1024 / 1024).toFixed(2)} MB · {f.mime_type}
                </div>
                <div className="text-xs text-neutral-500 mb-3">
                  Deleted: {new Date(f.deleted_at).toLocaleDateString()}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRecover(f.id)}
                    className="flex-1 px-3 py-1.5 rounded bg-green-600 hover:bg-green-500 text-white text-sm"
                  >
                    Recover
                  </button>
                  <button
                    onClick={() => handleDeleteForever(f.id)}
                    className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-sm"
                  >
                    Delete Forever
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}


